import { z } from 'zod';
import { CLASSIFICATIONS, EVIDENCE_STATUSES, SEVERITIES } from './labels';

const isoTimestamp = z.string().regex(
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/,
  'Expected an ISO-8601 UTC timestamp.',
);

const httpUrl = z.string().refine((value) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}, 'Expected an http or https URL.');

export const evidenceSchema = z.object({
  id: z.string().min(1).max(80),
  title: z.string().min(1).max(160),
  status: z.enum(EVIDENCE_STATUSES),
  detail: z.string().min(1).max(2000),
});

export const findingSchema = z.object({
  severity: z.enum(SEVERITIES),
  title: z.string().min(1).max(200),
  detail: z.string().min(1).max(2000),
});

export const linkSchema = z.object({
  id: z.string().min(1).max(80),
  label: z.string().min(1).max(200),
  url: httpUrl,
});

export const phpIndicatorSchema = z.object({
  type: z.enum(['header', 'cookie']),
  name: z.string().min(1).max(120),
  value: z.string().max(200).nullable(),
  strength: z.enum(['explicit', 'weak']),
});

export const technologySchema = z.object({
  name: z.string().min(1).max(80),
  detail: z.string().min(1).max(300),
});

export const scanSchema = z.object({
  submittedUrl: z.string().min(1).max(2048),
  finalUrl: z.string().min(1).max(2048).nullable(),
  scannedAt: isoTimestamp,
  durationMs: z.number().int().nonnegative().max(120_000),
  httpStatus: z.number().int().min(100).max(599).nullable(),
  classification: z.enum(CLASSIFICATIONS),
  classificationLabel: z.string().min(1).max(120),
  severity: z.enum(SEVERITIES),
  severityLabel: z.string().min(1).max(80),
  summary: z.string().min(1).max(4000),
  detectedPhpVersion: z.string().max(80).nullable(),
  phpBranch: z.string().max(20).nullable(),
  fixedVersion: z.string().max(40).nullable(),
  fixedVersionNote: z.string().max(800).nullable(),
  phpIndicators: z.array(phpIndicatorSchema).max(20),
  technologies: z.array(technologySchema).max(12),
  cves: z.array(linkSchema).min(2).max(10),
  findings: z.array(findingSchema).max(20),
  evidence: z.array(evidenceSchema).min(1).max(20),
  limitations: z.array(z.string().min(1).max(1000)).min(1).max(20),
  remediation: z.array(z.string().min(1).max(1200)).min(1).max(20),
  advisories: z.array(linkSchema).min(1).max(10),
  advisoryDataReviewedAt: isoTimestamp,
  disclaimer: z.string().min(1).max(2000),
  redirectsFollowed: z.number().int().nonnegative().max(10),
  bodyTruncated: z.boolean(),
});

export const scanSuccessSchema = z.object({
  success: z.literal(true),
  scan: scanSchema,
});

export const apiErrorSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.string().min(1).max(80),
    message: z.string().min(1).max(300),
  }),
});

export const scanRequestSchema = z
  .object({
    url: z.string().trim().min(1).max(2048),
    authorized: z.literal(true),
  })
  .strict();

export type ScanSuccess = z.infer<typeof scanSuccessSchema>;
export type ScanReport = ScanSuccess['scan'];
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;
export type EvidenceItem = z.infer<typeof evidenceSchema>;
export type Finding = z.infer<typeof findingSchema>;
export type PhpIndicator = z.infer<typeof phpIndicatorSchema>;

export class ScanPayloadError extends Error {
  constructor(message = 'The scanner returned an unexpected response.') {
    super(message);
    this.name = 'ScanPayloadError';
  }
}

export function parseScanPayload(payload: unknown): ScanSuccess {
  const parsed = scanSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new ScanPayloadError();
  }
  return parsed.data;
}

export function readErrorMessage(payload: unknown): string | null {
  const parsed = apiErrorSchema.safeParse(payload);
  if (!parsed.success) return null;
  return parsed.data.error.message;
}
